import { useMutation } from "@tanstack/react-query";
import signUpLogo from "../../assets/images/logo/Trellis_Logo.png";
import { Button } from "../../components/core/basic/Button";
import { HoverInfo } from "../../components/core/basic/Tooltip";
import { Checkbox } from "../../components/fields/base/Checkbox";
import { Select } from "../../components/fields/base/Select";
import { TextField } from "../../components/fields/base/TextField";
import { useState } from "react";
import { Label } from "react-aria-components";
import { Controller, useForm, useWatch } from "react-hook-form";
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from "react-phone-number-input";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAppDispatch } from "../../redux/hooks";
import { getAPIKeys } from "../../utils/getUrlPrefix";
import { trackFacebookPixelEvent } from "../../utils/trackFacebookPixelEvent";
import { trackGaEvent } from "../../utils/trackGaEvent";
import { trackLinkedinPixelEvent } from "../../utils/trackLinkedinPixelEvent";
import { apiClient } from "../../zodios/client";
import { css, cx } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import { formField } from "../../../styled-system/recipes";
import TermsOfService from "../../components/custom/instructions/TermsOfService";
import { tokenLogin } from "./tokens/tokenLogin";
import { getIsLocalhost } from "../../utils/isLocalHost";
import { useTranslation } from "react-i18next";
import { isPersistedLanguage } from "../../i18n/languages";

const apiKeys = getAPIKeys();

const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const CLIENT_SECRET = apiKeys["API_AUTH_SECRET"];

interface RegisterFormData {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
  phoneNumber: string;
  howDidYouHear: string | null;
  agreedToTerms: boolean;
}

const INITIAL_DATA: RegisterFormData = {
  firstName: "",
  lastName: "",
  companyName: "",
  email: "",
  password: "",
  phoneNumber: "",
  howDidYouHear: null,
  agreedToTerms: false,
};

const SignUpForm = ({ extensionSignup = false }: { extensionSignup?: boolean }) => {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  const { t, i18n } = useTranslation(["auth", "validation"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    mode: "onBlur",
    defaultValues: INITIAL_DATA,
  });

  const formState = useWatch({ control });

  const classes = formField();

  const registerMutation = useMutation({
    mutationFn: async (values: RegisterFormData) => {
      // Seed the account-wide language preference from the top-right switcher (or browser
      // default). Omitted unless it's a real persistable locale, so we never send `en-XA`.
      const language = i18n.resolvedLanguage;
      const signUpFormData = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email.trim(),
        password: values.password,
        organization_name: values.companyName.trim(),
        contact_number: values.phoneNumber.trim(),
        hear_about_us: values.howDidYouHear,
        terms_agreement: values.agreedToTerms,
        signup_source: extensionSignup ? "extension" : "app",
        ...(language && isPersistedLanguage(language) ? { language } : {}),
      };

      await apiClient.registerOrganization(signUpFormData);

      const loginRes = await apiClient.login({
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        grant_type: "password",
        username: values.email,
        password: values.password,
      });

      if (loginRes.requires_mfa) {
        throw new Error(t(($) => $.auth.signup["Authentication error, please contact support."]));
      }

      const { access_token, refresh_token } = loginRes;
      await tokenLogin({ access_token, refresh_token }, dispatch);

      trackGaEvent(localStorage.extensionSignup ? "Extension User Sign Up" : "App User Sign Up", "Signup");
      trackLinkedinPixelEvent();
      trackFacebookPixelEvent(localStorage.extensionSignup ? "Extension User Sign Up" : "App User Sign Up");

      void navigate({ to: "/onboarding/v2/about/type" });
    },
    onError: (res: { response?: { data?: { email?: string[]; password?: string[] } }; message?: string }) => {
      const errors = res.response?.data;

      if (errors?.email?.[0]) {
        setError("email", { type: "custom", message: errors.email[0] });
      }

      if (errors?.password?.[0]) {
        setError("password", { type: "custom", message: errors.password[0] });
      }

      if (!errors?.email && !errors?.password && res.message) {
        setError("email", { type: "custom", message: res.message });
      }
    },
  });

  const onSubmit = (values: RegisterFormData) => {
    if (registerMutation.isPending) {
      return;
    }
    registerMutation.mutate(values);
  };

  return (
    <Flex
      className={css({
        maxWidth: "100%",
        height: "100%",
        paddingBottom: "large",
      })}
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      <img
        src={signUpLogo}
        alt={t(($) => $.auth.signup["Trellis logo"])}
        className={css({
          width: "300px",
          paddingY: "xlarge",
        })}
      />
      <p>{t(($) => $.auth.signup["The beginning of your Trellis experience starts now."])}</p>
      <form
        id={getIsLocalhost() ? "LocalTestingSignUpForm" : "AppSignUpForm"}
        className={cx(
          css({
            display: "flex",
            gap: "large",
            flexDirection: "column",
            width: { base: "100%", md: "580px", lg: "620px" },
            padding: { base: "xlarge", lg: "inherit" },
          }),
          getIsLocalhost() ? "Local Testing" : "App Sign-Up Form",
        )}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Flex
          className={css({
            gap: "large",
            flexDirection: { base: "column", sm: "row" },
          })}
        >
          <Controller
            name={"firstName"}
            control={control}
            rules={{
              required: { value: true, message: t(($) => $.validation.firstName["First name is required"]) },
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={t(($) => $.auth.signup["First Name"])}
                type={"text"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name={"lastName"}
            control={control}
            rules={{
              required: { value: true, message: t(($) => $.validation.lastName["Last name is required"]) },
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={t(($) => $.auth.signup["Last Name"])}
                type={"text"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </Flex>
        <Controller
          name={"companyName"}
          control={control}
          rules={{
            required: { value: true, message: t(($) => $.validation.companyName["A company name is required"]) },
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={t(($) => $.auth.signup["Company Name"])}
              type={"text"}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name={"email"}
          control={control}
          rules={{
            required: { value: true, message: t(($) => $.validation.email["An email is required"]) },
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t(($) => $.validation.email["Invalid email address"]),
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={t(($) => $.auth.signup.Email)}
              type={"email"}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name={"password"}
          control={control}
          rules={{
            required: { value: true, message: t(($) => $.validation.password["A password is required"]) },
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={
                <span className={css({ display: "flex", gap: "small" })}>
                  {t(($) => $.auth.signup.Password)}{" "}
                  <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                    {t(
                      ($) =>
                        $.auth.signup[
                          "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number."
                        ],
                    )}
                  </HoverInfo>
                </span>
              }
              type={"password"}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <div>
          <Controller
            name={"phoneNumber"}
            control={control}
            rules={{
              required: { value: true, message: t(($) => $.validation.phoneNumber["A phone number is required"]) },
              validate: (value) =>
                isValidPhoneNumber(value) || t(($) => $.validation.phoneNumber["Please enter a valid phone number"]),
            }}
            render={({ field, fieldState }) => (
              <>
                <Label className={classes.label}>{t(($) => $.auth.signup["Phone Number"])}</Label>
                <PhoneInputWithCountrySelect
                  className={`phone_number_input ${css({ border: "1px solid #DDD", borderRadius: "2000000px", marginBottom: 0, paddingX: "large" })}`}
                  international
                  withCountryCallingCode
                  placeholder={t(($) => $.auth.signup["Contact Number"])}
                  onChange={(value) => field.onChange(value)}
                  onBlur={field.onBlur}
                />
                {fieldState.error && (
                  <p className={`${classes.error} ${css({ paddingY: "small" })}`}>{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
        <Controller
          name={"howDidYouHear"}
          control={control}
          rules={{
            required: { value: true, message: t(($) => $.validation.field["This field is required"]) },
          }}
          render={({ field, fieldState }) => (
            <Select
              label={t(($) => $.auth.signup["How Did You Hear About Us"])}
              onChange={(value) => {
                field.onChange(value);
              }}
              onBlur={field.onBlur}
              value={formState.howDidYouHear}
              items={[
                { label: t(($) => $.auth.signup.howDidYouHearOptions.Facebook), value: "Facebook" },
                { label: t(($) => $.auth.signup.howDidYouHearOptions.Google), value: "Google" },
                { label: t(($) => $.auth.signup.howDidYouHearOptions.Bing), value: "Bing" },
                { label: t(($) => $.auth.signup.howDidYouHearOptions.Reddit), value: "Reddit" },
                { label: t(($) => $.auth.signup.howDidYouHearOptions.WhatsApp), value: "WhatsApp" },
                { label: t(($) => $.auth.signup.howDidYouHearOptions.Referral), value: "Referral" },
                { label: t(($) => $.auth.signup.howDidYouHearOptions.YouTube), value: "YouTube" },
                {
                  label: t(($) => $.auth.signup.howDidYouHearOptions["Amazon Ads Directory"]),
                  value: "Amazon Ads Directory",
                },
              ]}
              errorMessage={fieldState.error?.message}
            />
          )}
        />

        <Flex
          className={css({ paddingY: "small", fontSize: "medium", whiteSpace: "nowrap" })}
          justifyContent={"center"}
        >
          <Checkbox
            value={formState.agreedToTerms as boolean}
            onChange={(checked) => setValue("agreedToTerms", checked)}
            className={css({ marginRight: "medium" })}
          />
          {t(($) => $.auth.signup["I agree to the"])} <TermsOfService justTerms={"showTerms"} />{" "}
          <a
            href={"https://gotrellis.com/privacy-policy/"}
            target={"_blank"}
            className={css({
              paddingX: "xsmall",
              color: "trellisPalette.0",
              textDecoration: "none",
              fontWeight: "bold",
            })}
            onClick={() => setShowPrivacyPolicy(!showPrivacyPolicy)}
            rel="noreferrer"
          >
            {t(($) => $.auth.signup["Privacy Policy"])}
          </a>
        </Flex>
        <Button
          type={"submit"}
          css={{ marginX: "auto", paddingX: "100px", fontSize: "larger", textTransform: "none" }}
          isDisabled={Object.values(formState).some((value) => !value) || !!Object.keys(errors).length}
          isPending={registerMutation.isPending}
        >
          {extensionSignup ? t(($) => $.auth.signup["Connect Extension"]) : t(($) => $.auth.signup["Sign Up"])}
        </Button>
        <div className={css({ marginY: "small", fontSize: "small", textAlign: "center" })}>
          {t(($) => $.auth.signup["Already a member?"])}{" "}
          <Link
            to={"/"}
            className={css({
              cursor: "pointer",
              color: "trellisPalette.0",
              textDecoration: "underline",
              fontWeight: "normal",
            })}
          >
            {t(($) => $.auth.signup["Sign In"])}
          </Link>
        </div>
      </form>
    </Flex>
  );
};

export default SignUpForm;
